/* CBH — Image with Text 2 (scroll-snap slider controller)
   Root-cause fix:
   - Removes content-visibility placeholder once section is near viewport
   - Uses measured scroll positions (not offsetLeft) for reliable navigation
   Theme Editor fixes:
   - Idempotent init (prevents double-binding)
   - Re-init on section load/select
   - Cleanup on unload
   - Extra refresh after fonts + full window load
*/
(() => {
  const prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function unlazyWhenVisible(root) {
    const outer = root.querySelector('.cbh-iwt2__outer');
    if (!outer || !outer.classList.contains('cbh-iwt2__outer--lazy')) return;

    const unlazy = () => {
      outer.classList.remove('cbh-iwt2__outer--lazy');
      // Force a layout pass so measurements are correct immediately.
      void outer.offsetHeight;
    };

    if (!('IntersectionObserver' in window)) {
      unlazy();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting || e.intersectionRatio > 0)) {
          unlazy();
          io.disconnect();
        }
      },
      // Start un-lazying slightly before it enters view
      { rootMargin: '250px 0px 250px 0px', threshold: 0.01 }
    );

    io.observe(outer);

    // Store for cleanup (Theme Editor swaps sections)
    root.__cbhIwt2IO = io;
  }

  function cleanup(root) {
    try {
      if (root.__cbhIwt2IO) {
        root.__cbhIwt2IO.disconnect();
        root.__cbhIwt2IO = null;
      }
    } catch (e) {}

    try {
      if (root.__cbhIwt2RO) {
        root.__cbhIwt2RO.disconnect();
        root.__cbhIwt2RO = null;
      }
    } catch (e) {}

    try {
      if (root.__cbhIwt2Timer) {
        clearInterval(root.__cbhIwt2Timer);
        root.__cbhIwt2Timer = null;
      }
    } catch (e) {}

    // Remove any bound handlers we stored
    if (root.__cbhIwt2Handlers) {
      const h = root.__cbhIwt2Handlers;

      try { h.viewport && h.onScroll && h.viewport.removeEventListener('scroll', h.onScroll); } catch (e) {}
      try { h.viewport && h.onKeydown && h.viewport.removeEventListener('keydown', h.onKeydown); } catch (e) {}

      try { h.prev && h.onPrev && h.prev.removeEventListener('click', h.onPrev); } catch (e) {}
      try { h.next && h.onNext && h.next.removeEventListener('click', h.onNext); } catch (e) {}

      try {
        (h.dots || []).forEach((dot, idx) => {
          const fn = (h.onDotClicks || [])[idx];
          if (fn) dot.removeEventListener('click', fn);
        });
      } catch (e) {}

      try {
        if (h.autoplay) {
          root.removeEventListener('mouseenter', h.stop);
          root.removeEventListener('mouseleave', h.start);
          root.removeEventListener('focusin', h.stop);
          root.removeEventListener('focusout', h.start);
          document.removeEventListener('visibilitychange', h.onVis);
        }
      } catch (e) {}

      root.__cbhIwt2Handlers = null;
    }

    // Allow re-init
    delete root.dataset.cbhIwt2Init;
  }

  function init(root) {
    if (!root) return;

    // ✅ Theme Editor / dynamic re-render protection
    if (root.dataset.cbhIwt2Init === 'true') return;
    root.dataset.cbhIwt2Init = 'true';

    const viewport = root.querySelector('.cbh-iwt2__viewport');
    const slides = Array.from(root.querySelectorAll('.cbh-iwt2__slide'));
    const dots = Array.from(root.querySelectorAll('.cbh-iwt2__dot'));
    const prev = root.querySelector('.cbh-iwt2__nav-btn[data-dir="prev"]');
    const next = root.querySelector('.cbh-iwt2__nav-btn[data-dir="next"]');

    if (!viewport || slides.length <= 1) return;

    unlazyWhenVisible(root);

    let active = 0;
    let raf = null;
    let positions = [];

    const setBtnState = () => {
      if (prev) prev.disabled = active <= 0;
      if (next) next.disabled = active >= slides.length - 1;
    };

    const setActive = (i) => {
      active = clamp(i, 0, slides.length - 1);
      dots.forEach((d, idx) =>
        d.setAttribute('aria-current', idx === active ? 'true' : 'false')
      );
      setBtnState();
    };

    const measure = () => {
      const vpRect = viewport.getBoundingClientRect();
      positions = slides.map((slide) => {
        const r = slide.getBoundingClientRect();
        return viewport.scrollLeft + (r.left - vpRect.left);
      });
    };

    const closestIndex = () => {
      const x = viewport.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < positions.length; i++) {
        const dist = Math.abs(positions[i] - x);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    };

    const goTo = (i) => {
      const idx = clamp(i, 0, slides.length - 1);
      const left = positions[idx] ?? 0;

      viewport.scrollTo({
        left,
        behavior: prefersReduced ? 'auto' : 'smooth'
      });

      setActive(idx);
    };

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setActive(closestIndex()));
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });

    const onDotClicks = [];
    dots.forEach((dot) => {
      const fn = () => {
        const i = Number(dot.dataset.dot || 0);
        goTo(i);
      };
      onDotClicks.push(fn);
      dot.addEventListener('click', fn);
    });

    const onPrev = () => goTo(active - 1);
    const onNext = () => goTo(active + 1);

    prev && prev.addEventListener('click', onPrev);
    next && next.addEventListener('click', onNext);

    const onKeydown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(active - 1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(active + 1);
      }
    };
    viewport.addEventListener('keydown', onKeydown);

    // Measure AFTER fonts/layout are ready
    const refresh = () => {
      measure();
      setActive(closestIndex());
    };

    // Initial paint sequence: do a couple of measures for stability
    requestAnimationFrame(() => {
      refresh();
      requestAnimationFrame(refresh);
    });

    // ✅ After fonts + full window load (helps editor and late-loading assets)
    try {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => requestAnimationFrame(refresh));
      }
    } catch (e) {}
    window.addEventListener('load', () => requestAnimationFrame(refresh), { once: true });

    // Resize changes (panel width changes affect positions)
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => refresh());
      ro.observe(viewport);
      slides.forEach((s) => ro.observe(s));
      root.__cbhIwt2RO = ro;
    } else {
      window.addEventListener('resize', refresh, { passive: true });
    }

    // Optional autoplay
    const autoplay = String(root.dataset.autoplay) === 'true';
    const interval = Math.max(2500, Number(root.dataset.interval || 6500));
    let timer = null;

    const start = () => {
      if (!autoplay) return;
      stop();
      timer = setInterval(() => goTo((active + 1) % slides.length), interval);
      root.__cbhIwt2Timer = timer;
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
        root.__cbhIwt2Timer = null;
      }
    };

    const onVis = () => {
      if (document.hidden) stop();
      else start();
    };

    if (autoplay) {
      start();
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', onVis);
    }

    // Store handlers for cleanup
    root.__cbhIwt2Handlers = {
      viewport,
      onScroll,
      onKeydown,
      prev,
      next,
      onPrev,
      onNext,
      dots,
      onDotClicks,
      autoplay,
      start,
      stop,
      onVis
    };
  }

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-cbh-iwt2]').forEach(init);
  };

  // Initial boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  // ✅ Shopify Theme Editor: section lifecycle support
  document.addEventListener('shopify:section:load', (e) => {
    const id = e.detail && e.detail.sectionId;
    if (!id) return;

    const root = document.querySelector(`[data-cbh-iwt2][data-section-id="${id}"]`);
    if (!root) return;

    // Ensure clean init
    cleanup(root);
    requestAnimationFrame(() => init(root));
  });

  document.addEventListener('shopify:section:select', (e) => {
    const id = e.detail && e.detail.sectionId;
    if (!id) return;

    const root = document.querySelector(`[data-cbh-iwt2][data-section-id="${id}"]`);
    if (!root) return;

    // When selected, force a refresh via resize (safe) to settle layout
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const id = e.detail && e.detail.sectionId;
    if (!id) return;

    const root = document.querySelector(`[data-cbh-iwt2][data-section-id="${id}"]`);
    if (!root) return;

    cleanup(root);
  });
})();
